import { IOperationResultNoValue, KeyDateKind } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useModal } from '../../../../../models/hooks/useModal';
import {
	AccountDetailsContext,
	AdminPermission,
	AdminUserSessionContext,
} from '../../../../../viewmodels/AdminViewModels';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../../web/components/ConfirmationDialog';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { TextInput } from '../../../../../web/components/TextInput';
import { KeyDateIcon } from '../../../../../web/components/svgs/icons/KeyDateIcon';
import { TrashIcon } from '../../../../../web/components/svgs/icons/TrashIcon';
import { WarningIcon } from '../../../../../web/components/svgs/icons/WarningIcon';
import { baseStyleSheet } from '../../../../../web/styles/styles';
import { ConditionalAccess } from '../../../../components/ConditionalAccess';
import { RestoreKeyFactsModal } from '../../../../components/RestoreKeyFactsModal';
import { SettingCategory } from './Setting';
import { DeleteKeyFactsConfirmationOptions } from './options';
import { SettingsColumns } from './presentation';
import { styleSheet } from './styles';

interface IProps extends RouteComponentProps<any> {
	className?: string;
	scrollToBottomPortalId?: string;
	styles?: StyleDeclarationValue[];
}

const SettingsFc: React.FC<IProps> = () => {
	const [settingsSearch, setSettingsSearch] = useState('');
	const [showRestoreKeyFactsModal, setShowRestoreKeyFactsModal] = useState(false);
	const adminUserSession = useUserSession<AdminUserSessionContext>();

	const accountDetailsContext = React.useContext(AccountDetailsContext);

	const canModifyAccount = !!adminUserSession.hasPermission(AdminPermission.ModifyAccount);
	const hasDevPermission = !!adminUserSession.hasPermission(AdminPermission.OnlyForDevelopers);
	const hasSupportPermission = !!adminUserSession.hasPermission(AdminPermission.SupportLevel3);

	const errorMessages = useErrorMessages();

	const deleteRenewalKeyFactsModal = useModal(
		false,

		(result: IConfirmationDialogOption<boolean>) => {
			if (result?.isDestructive) {
				accountDetailsContext.accountOperations
					.deleteKeyFacts(KeyDateKind.Renewal)
					?.then(() => {
						toast.success(`Renewal key facts deleted successfully`);
					})
					?.catch((error: IOperationResultNoValue) => {
						errorMessages.pushApiError(error);
					});
			}
		},
		[accountDetailsContext.accountOperations]
	);

	const deleteBirthdayKeyFactsModal = useModal(
		false,

		(result: IConfirmationDialogOption<boolean>) => {
			if (result?.isDestructive) {
				accountDetailsContext.accountOperations
					.deleteKeyFacts(KeyDateKind.Birthday)
					?.then(() => {
						toast.success(`Birthday key facts deleted successfully`);
					})
					?.catch((error: IOperationResultNoValue) => {
						errorMessages.pushApiError(error);
					});
			}
		},
		[accountDetailsContext.accountOperations]
	);

	const onClearSettingsSearch = () => {
		setSettingsSearch('');
	};

	const onSettingsSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSettingsSearch(e.target.value);
	};
	const onRestoreKeyFacts = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		setShowRestoreKeyFactsModal(true);
	};

	const onRestoreKeyFactsRequestClose = () => {
		setShowRestoreKeyFactsModal(false);
	};

	return (
		<>
			{accountDetailsContext.accountOperations.account ? (
				<>
					<ConditionalAccess permission={AdminPermission.ModifyAccount}>
						<div className={css(styleSheet.deleteKeyFactsHeader)}>
							<div className={css(styleSheet.settingsSearchContainer)}>
								<TextInput
									className={css(styleSheet.settingsSearch)}
									inputId='admin-settings-search'
									onChange={onSettingsSearch}
									placeholder='Search Settings'
									type='text'
									value={settingsSearch}
								/>
								{settingsSearch?.length > 1 ? (
									<button className={css(baseStyleSheet.brandLink)} onClick={onClearSettingsSearch}>
										Clear
									</button>
								) : null}
							</div>
							<div className={css(styleSheet.buttonsWrapper)}>
								<div>
									<button className={`${css(styleSheet.actionEntities)}`} onClick={onRestoreKeyFacts}>
										<KeyDateIcon className={css(styleSheet.columnButtonIcon)} />
										<span style={{ marginLeft: 10 }}>Restore Key Facts</span>
									</button>

									<RestoreKeyFactsModal
										userSession={adminUserSession}
										modalProps={{
											isOpen: showRestoreKeyFactsModal,
											onRequestClose: onRestoreKeyFactsRequestClose,
										}}
									/>
									<button
										className={`${css(styleSheet.actionEntities)}`}
										onClick={deleteRenewalKeyFactsModal.setIsOpen(true)}
									>
										<TrashIcon className={css(styleSheet.columnButtonIcon)} />
										<span style={{ marginLeft: 10 }}>Delete Renewal Key Facts</span>
									</button>
									<ConfirmationDialog
										className={css(styleSheet.deleteRenewKeyFactsModal)}
										icon={<WarningIcon />}
										modalProps={{
											isOpen: deleteRenewalKeyFactsModal.isOpen,
											onRequestClose: deleteRenewalKeyFactsModal.onRequestClose,
										}}
										options={DeleteKeyFactsConfirmationOptions}
										title='Are you sure you want to delete all renewal key facts for this account? This operation cannot be undone.'
									/>
									{accountDetailsContext.accountOperations.isDeletingKeyFacts && (
										<LoadingSpinner className={css(styleSheet.loading)} type='large' />
									)}
								</div>
								<div>
									<button
										className={`${css(styleSheet.actionEntities)}`}
										onClick={deleteBirthdayKeyFactsModal.setIsOpen(true)}
									>
										<TrashIcon className={css(styleSheet.columnButtonIcon)} />
										<span style={{ marginLeft: 10 }}>Delete Birthday Key Facts</span>
									</button>
									<ConfirmationDialog
										className={css(styleSheet.deleteRenewKeyFactsModal)}
										icon={<WarningIcon />}
										modalProps={{
											isOpen: deleteBirthdayKeyFactsModal.isOpen,
											onRequestClose: deleteBirthdayKeyFactsModal.onRequestClose,
										}}
										options={DeleteKeyFactsConfirmationOptions}
										title='Are you sure you want to delete all birthday key facts for this account? This operation cannot be undone.'
									/>
									{accountDetailsContext.accountOperations.isDeletingKeyFacts && (
										<LoadingSpinner className={css(styleSheet.loading)} type='large' />
									)}
								</div>
							</div>
						</div>
					</ConditionalAccess>
					<div className={`${css(styleSheet.overviewContainer)}`}>
						<SettingsColumns
							hasDevPermission={hasDevPermission}
							canModifyAccount={canModifyAccount}
							settingsSearch={settingsSearch}
							category={SettingCategory.Administrative}
						/>
						<SettingsColumns
							hasSupportPermission={hasSupportPermission}
							hasDevPermission={hasDevPermission}
							canModifyAccount={canModifyAccount}
							settingsSearch={settingsSearch}
							category={SettingCategory.Features}
						/>
						<SettingsColumns
							hasDevPermission={hasDevPermission}
							canModifyAccount={canModifyAccount}
							settingsSearch={settingsSearch}
							category={SettingCategory.EmailTracking}
						/>
						<SettingsColumns
							hasDevPermission={hasDevPermission}
							canModifyAccount={canModifyAccount}
							settingsSearch={settingsSearch}
							category={SettingCategory.Dashboard}
						/>
						<SettingsColumns
							hasDevPermission={hasDevPermission}
							canModifyAccount={canModifyAccount}
							settingsSearch={settingsSearch}
							category={SettingCategory.PreApprovedContent}
						/>
						<SettingsColumns
							hasSupportPermission={hasSupportPermission}
							hasDevPermission={hasDevPermission}
							canModifyAccount={canModifyAccount}
							settingsSearch={settingsSearch}
							category={SettingCategory.Databoards}
						/>
						<ConditionalAccess
							permission={AdminPermission.OnlyForDevelopers}
							or={[AdminPermission.SupportLevel3, AdminPermission.ResetAccount]}
						>
							<SettingsColumns
								hasDevPermission={hasDevPermission}
								hasSupportPermission={hasSupportPermission}
								canModifyAccount={canModifyAccount}
								settingsSearch={settingsSearch}
								category={SettingCategory.Permanent}
							/>
						</ConditionalAccess>
					</div>
				</>
			) : (
				<LoadingSpinner className={css(styleSheet.loading)} type='large' />
			)}
		</>
	);
};

const SettingsWithObserver = observer(SettingsFc);
export const Settings = withRouter(SettingsWithObserver);
